/**
 * Custom react-select Creatable dropdown renderer for SurveyJS.
 * Registered as "dropdown-react" renderAs for dropdown questions with addItem: true.
 * Supports mobile full-screen modal and desktop inline modes.
 */
import React from "react";
import { RendererFactory, ItemValue } from "survey-core";
import { SurveyQuestionDropdown, ReactQuestionFactory } from "survey-react-ui";
import Creatable from "react-select/creatable";
import { components } from 'react-select';
import i18n from 'i18next';
 

// Inline SVG component (avoids dependency on @svgr/webpack which is CRA-specific)
const DownArrowIcon = () => (
    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 6.062L0 1.062L1.062 0L5 3.938L8.938 0L10 1.062L5 6.062Z" fill="#222222"/>
    </svg>
);

class CustomSelect extends SurveyQuestionDropdown {
    constructor(props) {
        super(props);
        this.state = {
            isModalOpen: false,
            isMobile: window.innerWidth <= 1023,
            isHeightCalc: 0,
            isTooTall: false,
            isSearching: false
        };
        this.inputRef = React.createRef();
        this._searchDebounce = null;
        //to identify every search call made , to prevent showing stale data from previous call.
        this._searchSeq = 0;
        this._lastAppliedTerm = "";
    }

    componentWillUnmount() {
        if (this._searchDebounce) clearTimeout(this._searchDebounce);
    }

    get options() {
        const choices = this.question.visibleChoices;
        const hasSuggested = this.question.getPropertyValue("hasSuggestedCompanies");

        if (hasSuggested) {
            const suggested = [];
            const others = [];
            let passedSeparator = false;

            for (const c of choices) {
                if (c.value === "__separator__") {
                    passedSeparator = true;
                    continue;
                }
                const option = {
                    value: c.value,
                    label: c.text || c.value,
                    extra: c.extra || {}
                };
                if (!passedSeparator) {
                    suggested.push(option);
                } else {
                    others.push(option);
                }
            }

            const groups = [];
            if (suggested.length > 0) {
                groups.push({
                    label: "Suggested - based on your email",
                    options: suggested
                });
                groups.push({
                    label: "",
                    options: others
                });
            } else {
                return others;
            }
            return groups;
        }

        return choices.map((c) => ({
            value: c.value,
            label: c.text || c.value,
            extra: c.extra || {}
        }));
    }

    get flatOptions() {
        const opts = this.options;
        // Reducing grouped options to a flat array for easier searching and selection
        if (opts.length > 0 && opts[0]?.options) {
            return opts.reduce((acc, group) => acc.concat(group.options), []);
        }
        return opts;
    }

    get selectedOption() {
        const value = this.question.value;
        if (value === undefined || value === null || value === "") return [];

        const match = this.flatOptions.filter((o) => o.value == value);
        if (match.length > 0) return match;

        const label = this.question.adSelectedLabel || value;
        return [{ value, label }];
    }

    onSelectChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : null;
        this.question.adSelectedLabel = selectedOption ? (selectedOption.label || selectedOption.value) : null;
        // Reset the applied-term tracking so the next real search isn't skipped.
        this._lastAppliedTerm = "";
        this.setValueCore(value);
    };

    onCreateOption = (inputValue) => {
        if (!inputValue.trim()) return;

        // Cancel any in-flight/debounced search so a late-arriving response can't
        // overwrite question.choices and wipe out the company we're about to add.
        if (this._searchDebounce) {
            clearTimeout(this._searchDebounce);
            this._searchDebounce = null;
        }
        // Invalidate any already-fired search so its response is ignored on arrival.
        this._searchSeq++;
        this._lastAppliedTerm = "";

        const exists = this.question.choices.some(
            (choice) => choice.value === inputValue
        );
        if (!exists) {
            const newChoice = new ItemValue({
                value: inputValue,
                text: inputValue,
                extra: { isDisplayAddrLine1: "", isDisplayPostCode: "", isDisplayTown: "" }
            });
            this.question.choices = [...this.question.choices, newChoice];
        }

        // Remember the manually-added value so subsequent searches preserve it.
        this.question.adCreatedValue = inputValue;
        this.question.adSelectedLabel = inputValue;

        this.setValueCore(inputValue);
        this.setState({ isSearching: false });
        this.closeModal();
    };

    
    handleInputChange = (inputValue, actionMeta) => {

        // react-select fires onInputChange with an empty value on non-typing actions too, so to prevent such calls
      
        const action = actionMeta?.action;
        if (action && action !== "input-change") {
            return inputValue;
        }

        if (typeof this.question.searchEndpoint === "function") {
            const term = inputValue || "";

        
            if (term === this._lastAppliedTerm && !this._searchDebounce) {
                return inputValue;
            }

          
            if (this._searchDebounce) clearTimeout(this._searchDebounce);

            this._searchDebounce = setTimeout(() => {
                this._searchDebounce = null;
                const seq = ++this._searchSeq;
                this.setState({ isSearching: true });

                this.question.searchEndpoint(term).then(() => {
                  
                    if (seq !== this._searchSeq) return;

                    //Only show current response.
                    this._lastAppliedTerm = term;
                    this.setState({ isSearching: false });
                }).catch(() => {
                    if (seq === this._searchSeq) this.setState({ isSearching: false });
                });
            }, 300);
        }
        return inputValue;
    };

    //filter options to prevent any stale values. 
    filterOption = (option, rawInput) => {
        //Add New - company on dropdown feature to persis always in the dropdown 
        if (option?.__isNew__ || option?.data?.__isNew__) return true;

        const input = (rawInput || "").trim().toLowerCase();
        if (!input) return true;
        // Never hide a value that is already selected (keeps the current value shown).
        if (this.question.value != null && option.value === this.question.value) return true;
        const label = (option.label || option.value || "").toString().toLowerCase();
        return label.includes(input);
    };

    openModal = () => {
        const menuList = this.inputRef.current?.menuListRef;
        const selectInput = this.inputRef.current?.controlRef?.offsetHeight;
        const windowHeight = window.innerHeight;

        setTimeout(() => {
            if (menuList) {
                const dropdownHeight = menuList.offsetHeight + selectInput;
                const isTooTall = dropdownHeight > windowHeight;
                let theff = windowHeight - 120;

                if (!isTooTall) {
                    this.setState({ isHeightCalc: dropdownHeight, isTooTall: isTooTall });
                } else {
                    this.setState({ isHeightCalc: theff, isTooTall: isTooTall });
                }
            }
        }, 0);

        this.setState({ isModalOpen: true }, () => {
            this.inputRef?.current?.focus();
        });
    };

    closeModal = () => {
        this.setState({ isModalOpen: false, isHeightCalc: 0 });
    };

    handleModal = () => {
        this.closeModal();
    };

    renderElement() {
        if (this.isDisplayMode) {
            return (
                <div
                    id={this.question.inputId}
                    className={this.question.getControlClass()}
                    disabled>
                    {this.question.displayValue || this.question.placeholder}
                </div>
            );
        }

        const isMobile = this.state?.isMobile;
        const isTooTall = this.state?.isTooTall;
        const customAddText = this.question;
        const createText = customAddText.getPropertyValue("createText") || <span>{i18n.t('surveyjs.customdropdown.addItem')}</span>;
        const panelHeight = this.state.isHeightCalc;

        const noInlineStyles = {
            control: (provided) => ({
                ...provided,
                backgroundColor: "inherit",
            }),
            menu: (provided) => ({
                ...provided,
                ...(isTooTall && { height: `${panelHeight - 81}px` })
            }),
            input: (provided) => ({
                ...provided,
                fontSize: "unset"
            }),
            placeholder: (provided) => ({
                ...provided,
                fontSize: "unset"
            }),
            group: (provided, state) => ({
                ...provided,
                // Add bottom border (separator line) after the suggested group
                ...(state.data.label === "Suggested - based on your email" && {
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "8px",
                    marginBottom: "4px"
                })
            }),
            groupHeading: (provided) => ({
                ...provided,
                fontSize: "12px",
                color: "#666",
                fontWeight: "normal",
                fontStyle: "italic",
                textTransform: "none",
                paddingBottom: "4px"
            }),
        };

        const CustomDropdownIndicator = (props) => {
            return (
                <components.DropdownIndicator {...props}>
                    <DownArrowIcon />
                </components.DropdownIndicator>
            );
        };

        if (isMobile) {
            return (
                <>
                    <Creatable
                        isClearable
                        required={this.question.isRequired}
                        value={this.selectedOption}
                        placeholder={this.question?.placeholder || i18n.t('surveyjs.customdropdown.placeholder')}
                        onFocus={this.openModal}
                        onInputChange={this.handleInputChange}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        className="customDropdown"
                        classNamePrefix="addItemdropdown"
                        id={this.question.inputId}
                    />
                    {this.state.isModalOpen && (
                        <div className="mobile-wrapper" onClick={this.handleModal}>
                            <div className="mobile" style={{ height: `${panelHeight}px` }} onClick={(e) => e.stopPropagation()}>
                                <Creatable
                                    isClearable
                                    isSearchable
                                    components={{ DropdownIndicator: CustomDropdownIndicator }}
                                    autofocus
                                    className="customDropdown"
                                    id={this.question.inputId}
                                    value={this.selectedOption}
                                    onChange={this.onSelectChange}
                                    onCreateOption={this.onCreateOption}
                                    onFocus={this.openModal}
                                    onInputChange={this.handleInputChange}
                                    filterOption={this.filterOption}
                                    options={this.options}
                                    isLoading={this.state.isSearching}
                                    required={false}
                                    classNamePrefix="addItemdropdown"
                                    placeholder={this.question?.placeholder || i18n.t('surveyjs.customdropdown.placeholder')}
                                    styles={noInlineStyles}
                                    formatCreateLabel={(inputValue) => (
                                        <>
                                            <button onClick={this.closeModal} className="close-button"><strong>{i18n.t('surveyjs.customdropdown.close')}</strong></button>
                                            <span className="addItemFormat">{createText}<strong>{inputValue}</strong></span>
                                        </>
                                    )}
                                    isValidNewOption={() => true}
                                    menuIsOpen={true}
                                    ref={this.inputRef}
                                />
                            </div>
                        </div>
                    )}
                </>
            );
        }

        return (
            <Creatable
                isClearable
                isSearchable
                components={{ DropdownIndicator: CustomDropdownIndicator }}
                className="customDropdown"
                id={this.question.inputId}
                value={this.selectedOption}
                onChange={this.onSelectChange}
                onCreateOption={this.onCreateOption}
                onInputChange={this.handleInputChange}
                filterOption={this.filterOption}
                options={this.options}
                isLoading={this.state.isSearching}
                required={this.question.isRequired}
                classNamePrefix="addItemdropdown"
                placeholder={this.question?.placeholder || i18n.t('surveyjs.customdropdown.placeholder')}
                styles={noInlineStyles}
                formatCreateLabel={(inputValue) => (
                    <span className="addItemFormat">{createText}<strong>{inputValue}</strong></span>
                )}
                isValidNewOption={() => true}
            />
        );
    }
}

// Register the CustomSelect component
ReactQuestionFactory.Instance.registerQuestion("sv-dropdown-react", (props) => {
    return React.createElement(CustomSelect, props);
});

RendererFactory.Instance.registerRenderer(
    "dropdown",
    "dropdown-react",
    "sv-dropdown-react"
);

export default CustomSelect;
